import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { UserPlus, LogIn, Trophy, Users, BarChart3, User, Sparkles } from 'lucide-react';

// Protected route wrapper for gaming/social features
export const GuestRestricted = ({ children, feature }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isSpanish } = useLanguage();
  const navigate = useNavigate();
  
  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  // If authenticated, show the actual content
  if (isAuthenticated) {
    return children;
  }
  
  // Guest user - show sign up prompt
  const featureDetails = {
    friends: {
      icon: Users,
      titleEn: 'Friends & Social',
      titleEs: 'Amigos y Social',
      descEn: 'Connect with friends, challenge them, and build streaks together!',
      descEs: '¡Conéctate con amigos, desafíalos y construye rachas juntos!',
      benefits: [
        { en: 'Add friends and see their progress', es: 'Agrega amigos y ve su progreso' },
        { en: 'Challenge friends to learning duels', es: 'Desafía a amigos a duelos de aprendizaje' },
        { en: 'Build friend streaks for bonus XP', es: 'Construye rachas de amigos para XP extra' },
      ]
    },
    statistics: {
      icon: BarChart3,
      titleEn: 'Your Statistics',
      titleEs: 'Tus Estadísticas',
      descEn: 'Track your learning journey with detailed stats and progress!',
      descEs: '¡Sigue tu viaje de aprendizaje con estadísticas detalladas!',
      benefits: [
        { en: 'Track phrases viewed and favorites', es: 'Rastrea frases vistas y favoritas' },
        { en: 'See your daily and weekly streaks', es: 'Ve tus rachas diarias y semanales' },
        { en: 'Monitor your XP and level progress', es: 'Monitorea tu XP y progreso de nivel' },
      ]
    },
    leaderboard: {
      icon: Trophy,
      titleEn: 'Leaderboards',
      titleEs: 'Clasificaciones',
      descEn: 'Compete with others and climb the ranks!',
      descEs: '¡Compite con otros y sube en las clasificaciones!',
      benefits: [
        { en: 'See where you rank among all users', es: 'Ve tu posición entre todos los usuarios' },
        { en: 'Earn badges and achievements', es: 'Gana insignias y logros' },
        { en: 'Weekly and monthly competitions', es: 'Competencias semanales y mensuales' },
      ]
    },
    profile: {
      icon: User,
      titleEn: 'Your Profile',
      titleEs: 'Tu Perfil',
      descEn: 'Customize your profile and track your achievements!',
      descEs: '¡Personaliza tu perfil y rastrea tus logros!',
      benefits: [
        { en: 'Choose your avatar and display name', es: 'Elige tu avatar y nombre' },
        { en: 'Earn and display badges', es: 'Gana y muestra insignias' },
        { en: 'Share your invite code', es: 'Comparte tu código de invitación' },
      ]
    },
    challenges: {
      icon: Sparkles,
      titleEn: 'Challenges',
      titleEs: 'Desafíos',
      descEn: 'Take on daily, weekly, and monthly challenges!',
      descEs: '¡Acepta desafíos diarios, semanales y mensuales!',
      benefits: [
        { en: 'Complete challenges for bonus XP', es: 'Completa desafíos para XP extra' },
        { en: 'Compete with friends', es: 'Compite con amigos' },
        { en: 'Unlock special rewards', es: 'Desbloquea recompensas especiales' },
      ]
    }
  };
  
  const details = featureDetails[feature] || featureDetails.profile;
  const IconComponent = details.icon;
  
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <section className="py-12 px-4">
        <div className="container max-w-lg mx-auto">
          <Card className="border-2 border-[#00E5CC]/30 shadow-lg">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-[#00E5CC]/20 to-[#7C3AED]/20 flex items-center justify-center mb-4">
                <IconComponent className="h-8 w-8 text-[#00E5CC]" />
              </div>
              <CardTitle className="text-2xl">
                {isSpanish ? details.titleEs : details.titleEn}
              </CardTitle>
              <CardDescription className="text-base">
                {isSpanish ? details.descEs : details.descEn}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Benefits list */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-muted-foreground">
                  {isSpanish ? 'Crea una cuenta para:' : 'Create an account to:'}
                </p>
                <ul className="space-y-2">
                  {details.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-[#00E5CC] mt-0.5">✓</span>
                      <span>{isSpanish ? benefit.es : benefit.en}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* CTA Buttons */}
              <div className="space-y-3 pt-4">
                <Button 
                  className="w-full bg-[#00E5CC] hover:bg-[#00C9B2] text-[#0d1f2d]"
                  onClick={() => navigate('/register')}
                  data-testid="guest-signup-btn"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  {isSpanish ? 'Crear Cuenta Gratis' : 'Create Free Account'}
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/login')}
                  data-testid="guest-login-btn"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  {isSpanish ? '¿Ya tienes cuenta? Inicia Sesión' : 'Already have an account? Sign In'}
                </Button>
              </div>
              
              {/* Free features note */}
              <p className="text-xs text-center text-muted-foreground pt-2">
                {isSpanish 
                  ? '✨ La cuenta es gratis • Todas las frases • Lista de tareas • Temporizador'
                  : '✨ Account is free • All phrases • To-do list • Timer'}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default GuestRestricted;
